/**
 * @author Hiroki Moto
 * @description
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import equals from 'ramda/src/equals';
import filter from 'ramda/src/filter';
import isEmpty from 'ramda/src/isEmpty';
import keys from 'ramda/src/keys';
import prepend from 'ramda/src/prepend';
import uniq from 'ramda/src/uniq';
import cx from 'classnames';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import Icon from '@components/@core/Icon';
import AppActions from '~/actions/app';
import { UserRoles, SPECIALOPTIONS } from '~/services/config';
import Header from './UserHeader';

const AllEmployees = { id: -1, profile: { first_name: 'All', last_name: 'Employees' } };

const InitialEmployee = {
    department_id: [SPECIALOPTIONS.ALL],
    job_role_id: [SPECIALOPTIONS.ALL],
    search: '',
    team_id: [SPECIALOPTIONS.ALL],
    users: [SPECIALOPTIONS.ALL],
};

const Filter = ({
    className,
    filters = ['company', 'employee'],
    aligns = ['left', 'left'],
    defaultIds = [[], []],
    disabled = [[], []],
    withAllEmployees = false,
    multi = [false, true],
    mountEvent = false,
    dataCy,
    addPrefix = 'Add',
    add = '',
    back = '',
    renderRight = undefined,
    onAdd = () => {},
    onBack = undefined,
    onChange = () => {},
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const role = useSelector((state) => state.app.app_role_id);
    const userId = useSelector((state) => state.app.id);
    const companyId = useSelector((state) => state.app.company_info.id);

    const hasAllCompany = role < UserRoles.EMPLOYEE && role > UserRoles.SUPER_ADMIN;
    const appCompanies = useSelector((state) => state.app.companies);

    const companies = hasAllCompany ? prepend({ id: SPECIALOPTIONS.ALL, name: 'All Companies' }, appCompanies) : appCompanies;
    const employees = useSelector((state) => state.app.users);

    const company = useSelector((state) => state.app.selectedCompany);
    const employee = useSelector((state) => state.app.selectedEmployee);

    const [defaults, setDefaults] = useState([defaultIds[0].length ? defaultIds[0] : company, defaultIds[1].length ? defaultIds[1] : employee.users]);
    const [disable, setDisable] = useState(disabled);

    const handleBack = () => {
        if (onBack === undefined) {
            navigate(-1);
            return;
        }
        onBack();
    };

    const handleChange = (type) => (e) => {
        if (type === 'company') {
            if (e.length < 1) return;
            const ID = e[0].id;
            if (ID === SPECIALOPTIONS.ALL) {
                // User select All Companies option
                const companyIds = companies.map((item) => item.id);
                disable[0].push(SPECIALOPTIONS.ALL);
                disable[0] = uniq(disable[0]);
                const INDIVIDUAL = [userId];
                dispatch(AppActions.globalfilterRequest([ID], employee));
                dispatch(AppActions.getemployeesearchRequest({ companyId: companyIds }, false));
                setDisable(disable);
                setDefaults([[ID], INDIVIDUAL]);
            } else {
                // User select companies
                const companyIds = e.map((item) => item.id);
                disable[0] = filter((e) => e !== SPECIALOPTIONS.ALL && e == SPECIALOPTIONS.LIST, disable[0]);
                dispatch(AppActions.globalfilterRequest(companyIds, InitialEmployee));
                dispatch(AppActions.getemployeesearchRequest({ companyId: companyIds }, false));
                setDisable(disable);
                setDefaults([companyIds, defaults[1]]);
            }
            dispatch(AppActions.postmulticompanydataRequest());
            onChange(type, e);
        } else {
            if (e.length < 1) {
                // Use has not selected anyone.
                onChange(type, e);
            } else {
                // User select normal Employees
                const employeeIds = e.map((item) => item.id);
                setDefaults([defaults[0], employeeIds]);
                const _employee = { ...employee, users: employeeIds };
                dispatch(AppActions.globalfilterRequest(company, _employee));
                onChange(type, e);
            }
        }
    };

    const handleEmployee = (e) => {
        const _employee = { ...employee, ...e };
        dispatch(AppActions.globalfilterRequest(company, _employee));
        const payload = { companyId: company };
        keys(e).map((key) => {
            if (!isEmpty(e[key]) && !equals(e[key], [-1])) payload[key] = e[key];
        });
        dispatch(AppActions.getemployeesearchRequest(payload));
    };

    const handleSelectAll = () => {
        handleChange('employee')(employees);
        const userIds = employees.map((item) => item.id);
        setDefaults([defaults[0], userIds]);
        onChange('employee', employees);
        dispatch(AppActions.globalfilterRequest(company, employee));
    };

    const handleClear = () => {
        handleChange('employee')([]);
        setDefaults([company, employee.users]);
        handleEmployee(InitialEmployee);
        dispatch(
            AppActions.globalfilterRequest(company, {
                ...InitialEmployee,
                users: employee.users,
            })
        );
    };

    const emData = withAllEmployees ? [AllEmployees, ...employees] : employees;
    const emVis = { visibility: role > UserRoles.MANAGER ? 'hidden' : 'visible' };

    return (
        <div className={cx('core-filter page-filter mb-2', className)} data-cy={dataCy}>
            <div className="d-flex">
                {filters.map((item, index) => (
                    <div className="d-h-start" key={`core-filter-${index}`}>
                        {item === 'company' && (
                            <div className="d-h-start"  style={emVis}>
                                <Dropdown
                                    key={`core-filter-${item}`}
                                    align={aligns[index]}
                                    disabledOptions={disable[index]}
                                    data={companies}
                                    multi={multi[index]}
                                    clearIds={[companyId]}
                                    defaultIds={defaults[index]}
                                    getValue={(e) => e?.name}
                                    returnBy="data"
                                    mountEvent={mountEvent}
                                    onChange={handleChange('company')}
                                />
                            </div>
                        )}

                        {item === 'employee' && (
                            <div className="d-h-start" style={emVis}>
                                <Dropdown
                                    className="employee"
                                    key={`core-filter-${item}`}
                                    align={aligns[index]}
                                    disabledOptions={disable[index]}
                                    data={emData}
                                    multi={multi[index]}
                                    defaultIds={defaults[index]}
                                    noOptionLabel="Select"
                                    getValue={(e) => `${e?.profile?.first_name} ${e?.profile?.last_name}`}
                                    returnBy="data"
                                    mountEvent={mountEvent}
                                    header={<Header multi={multi[index]} onChange={handleEmployee} onSelectAll={handleSelectAll} />}
                                    onClear={handleClear}
                                    onChange={handleChange('employee')}
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {add !== '' && role < UserRoles.MANAGER && (
                <Button type="low" size="small" className={cx('d-h-end mb-0 pe-5 ps-3', add == 'esign' && 'text-capitalize')} onClick={onAdd}>
                    <Icon name="fas fa-plus me-3" size={12} color="#376caf" />
                    <div className="d-none d-lg-block">{`${addPrefix} ${add == 'esign' ? 'eSign' : add}`}</div>
                </Button>
            )}
            {back !== '' && (
                <Button className="d-h-end mb-0 pe-4 ps-3 arrow-btn" type="low" size="small" onClick={handleBack}>
                    <Icon name="fas fa-arrow-left me-3" size={12} color="#376caf" />
                    <div className="d-none d-lg-block text-300">{back == 'Back' ? 'Back' : `Back to ${back}`}</div>
                </Button>
            )}
            {renderRight}
        </div>
    );
};

Filter.propTypes = {
    className: PropTypes.string,
    /**
     * Usage: Determine what kind of dropdowns would be populated.
     * Description: We might have 2 dropdowns, Company and Employee.
     * Example: filters={['company']}
     * Expected: You would see only Company dropdown.
     */
    filters: PropTypes.array,
    /**
     * Usage: Default options
     * Example: defaultIds={[-1], [-1]}
     * Expected: The options that ID equals -1 would be selected in Company and Employee initially.
     * Exeption: If defaultIds is empty, the default options would be selected with the user's info automatically.
     * For instance, if your company is DrivingSales, your name is Jared Hamilton, you will see DrivingSales in Company, Jared Hamilton in Employee initially.
     */
    defaultIds: PropTypes.array,
    withAllEmployees: PropTypes.bool,
    /** Usage: Add All Employees option in the Employee dropdown */
    aligns: PropTypes.array,
    /**
     * Usage: Disable options.
     * Example: disabled={[[-1], [-1]]}
     * Expected: You can't handle options that ID equals -1 in Company and Employee dropdowns.
     */
    disabled: PropTypes.array,
    /**
     * Usage: Make dropdowns multi-selectable.
     * You can select multiple options.
     */
    multi: PropTypes.array,
    /**
     * Usage: Trigger onChange event at the initial stage.
     */
    mountEvent: PropTypes.bool,
    renderRight: PropTypes.node,
    addPrefix: PropTypes.string,
    add: PropTypes.string,
    back: PropTypes.string,
    onAdd: PropTypes.func,
    onBack: PropTypes.func,
    onChange: PropTypes.func,
};

export default Filter;
